import React, { useState, useEffect } from 'react';
import { API_BASE_URL, apiFetch } from './config';

const ReportProblem = ({ username: propUsername, onClose, logAction }) => {
  const [criticality, setCriticality] = useState('medium');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Prevent body scroll while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('Please describe the problem before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    // ALWAYS get username fresh from localStorage at submit time (most reliable)
    const currentUsername = localStorage.getItem('username') || propUsername || '';
    
    // Debug: log what we're sending and where it came from
    console.log('ReportProblem - propUsername:', propUsername);
    console.log('ReportProblem - localStorage username:', localStorage.getItem('username'));
    console.log('ReportProblem - using username:', currentUsername);
    console.log('Submitting report with:', { username: currentUsername, criticality, problem_description: description.trim() });

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/reportproblem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: currentUsername,
          criticality: criticality,
          problem_description: description.trim()
        })
      });

      if (response.ok) {
        setSubmitted(true);
        // Log the action
        if (logAction) {
          logAction('Report Problem Submitted', criticality, description.substring(0, 100));
        }
      } else {
        const errorText = await response.text();
        setError(`Failed to submit: ${errorText || 'Server error'}`);
      }
    } catch (err) {
      console.error('Error submitting problem report:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success view after submission
  if (submitted) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" style={{ width: '450px', maxWidth: '90%', textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
          <h2 style={{ color: '#22c55e', marginBottom: '15px' }}>Problem Reported</h2>
          <p style={{ color: '#9ca3af', marginBottom: '25px' }}>
            Thank you for your feedback! Your report has been logged and will be reviewed.
          </p>
          <button
            onClick={() => { window.scrollTo(0, 0); onClose(); }}
            style={{
              padding: '12px 30px',
              backgroundColor: '#238636',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: '500px', maxWidth: '90%' }}>
        <button onClick={() => { window.scrollTo(0, 0); onClose(); }} className="close-modal-btn" style={{ float: 'right' }}>×</button>
        
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <span style={{ fontSize: '28px' }}>⚠️</span> Report Issue
        </h2>
        
        <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
          Found a bug or have feedback? Let us know and we'll look into it.
        </p>

        {/* Criticality Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#e0e0e0' }}>
            Criticality Level:
          </label>
          <div style={{ display: 'flex', gap: '10px' }}>
            {['low', 'medium', 'high'].map((level) => (
              <button
                key={level}
                onClick={() => setCriticality(level)}
                style={{
                  flex: 1,
                  padding: '10px 15px',
                  backgroundColor: criticality === level 
                    ? (level === 'low' ? '#22c55e' : level === 'medium' ? '#f59e0b' : '#ef4444')
                    : '#2d2d2d',
                  color: criticality === level ? '#fff' : '#9ca3af',
                  border: criticality === level 
                    ? 'none' 
                    : '1px solid #444',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: criticality === level ? 'bold' : 'normal',
                  textTransform: 'capitalize',
                  transition: 'all 0.2s'
                }}
              >
                {level === 'low' && '🟢 '}
                {level === 'medium' && '🟡 '}
                {level === 'high' && '🔴 '}
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Description Textarea */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#e0e0e0' }}>
            Describe the Problem:
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please describe what happened, what you expected, and any steps to reproduce the issue..."
            style={{
              width: '100%',
              minHeight: '150px',
              padding: '12px',
              backgroundColor: '#1e1e1e',
              color: '#e0e0e0',
              border: '1px solid #444',
              borderRadius: '6px',
              resize: 'vertical',
              fontSize: '14px',
              lineHeight: '1.5',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '10px 15px',
            backgroundColor: '#ef444420',
            borderLeft: '4px solid #ef4444',
            borderRadius: '4px',
            marginBottom: '15px',
            color: '#ef4444'
          }}>
            {error}
          </div>
        )}

        {/* Submit Button */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => { window.scrollTo(0, 0); onClose(); }}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#9ca3af',
              border: '1px solid #444',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '12px 24px',
              backgroundColor: submitting ? '#4a4a4a' : '#f59e0b',
              color: '#000',
              border: 'none',
              borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportProblem;
